import React, {useEffect, useState} from 'react';
import {Button} from '@/components/ui';
import {Card, CardContent, CardHeader} from '@/components/ui/card.js';
import backendApis from "@/services/backendApis.js";
import {useKayAuth} from "@/context/AuthContext.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog.js";
import {toast} from "sonner";
import {Skeleton} from "@/components/ui/skeleton.js";
import Loading from "@/components/Loading.js";
import ConnectionCreation from "@/components/Settings/ConnectionCreation.js";
import constants from "@/constants/constants.js";
import {carrierSecretKey, formatTitle, getLocalStorage} from "@/helpers/helperFunctions.js";
import {CarrierAccess} from "@/types/org.js";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table.js";
import {ConnectionStatus} from "@/types/index.js";
import {connectedStatusColors} from "@/helpers/colors.js";
import {Badge} from "@/components/ui/badge.js";
import SimpleConnectionCreation from "@/components/Settings/SimpleConnectionCreation.js";

export type CarrierConnectionStatus = {
  browserStateExists: boolean,
  connected: boolean,
  credentialsExists: boolean
  loading: boolean,
}

const Connections: React.FC = () => {
  const {organization, user} = useKayAuth();
  const userId = user?.user_id;

  const [connectionStatus, setConnectionStatus] = useState<Record<string, CarrierConnectionStatus>>({});

  const allCarrierAccess = user?.carrier_access || [];

  const [createConnectionCarrier, setCreateConnectionCarrier] = useState<CarrierAccess | null>(null);

  useEffect(() => {
    fetchConnectionStatus();
  }, [organization]);

  // Check if secret exists in local storage
  const checkLocalSecretExists = (carrierName: string) => {
    const encryptedSecret = getLocalStorage(carrierSecretKey(carrierName));
    return !!encryptedSecret;
  };

  const isStoreLocal = (carrierName: string) => {
    const orgCarrier = allCarrierAccess.find(c => c.carrier_name === carrierName);
    return orgCarrier?.credential_storage === constants.localFlag;
  }

  const fetchConnectionStatus = async () => {
    if (!organization) {
      return;
    }

    const statusPromises = allCarrierAccess.map(async (carrier) => {
      const carrierName = carrier.carrier_name;
      const storeLocal = isStoreLocal(carrier.carrier_name);

      const params1 = {credentials_name: carrierName, user_id: userId};
      const params2 = {carrier_name: carrierName, user_id: userId};
      // const browserStateExists = await backendApis.browserStateExists(params2)
      const browserStateExists = {data: {result: true}};
      const result: any = {
        loading: false,
        name: carrier.carrier_name
      };
      result.browserStateExists = browserStateExists.data.result;
      if (storeLocal) {
        result.credentialsExists = checkLocalSecretExists(carrierName);
      } else {
        const response = await backendApis.secretExists(params1);
        result.credentialsExists = response?.data?.result;
      }
      return result;
    });
    const results = await Promise.all(statusPromises);
    const status: Record<string, CarrierConnectionStatus> = {};
    results.forEach((result) => {
      if (result) {
        status[result.name] = {
          browserStateExists: result.browserStateExists,
          connected: result.credentialsExists && result.browserStateExists,
          credentialsExists: result.credentialsExists,
          loading: false
        };
      }
    });
    setConnectionStatus(status);
  };

  const updateCarrierStatus = async (carrier: CarrierAccess) => {
    const carrierName = carrier.carrier_name;
    const storeLocal = isStoreLocal(carrier.carrier_name);
    const params1 = {credentials_name: carrierName, user_id: userId};
    const params2 = {carrier_name: carrierName, user_id: userId};
    // const browserStateExists = await backendApis.browserStateExists(params2)
    const browserStateExists = {data: {result: true}};
    const result: any = {
      loading: false,
      name: carrier.carrier_name,
    };
    result.browserStateExists = browserStateExists.data.result;
    if (storeLocal) {
      result.credentialsExists = checkLocalSecretExists(carrierName);
    } else {
      const response = await backendApis.secretExists(params1);
      result.credentialsExists = response?.data?.result;
    }
    setConnectionStatus((prev) => (
      {
        ...prev,
        [result.name]: {
          browserStateExists: result.browserStateExists,
          connected: result.credentialsExists && result.browserStateExists,
          credentialsExists: result.credentialsExists,
          loading: false
        }
      }
    ));
    setCreateConnectionCarrier(null);
  }

  const handleRemove = async (carrier: string) => {
    setConnectionStatus(prev => ({...prev, [carrier]: {...prev[carrier], loading: true}}));
    const storeLocal = isStoreLocal(carrier);
    if (storeLocal) {
      localStorage.removeItem(carrierSecretKey(carrier));
    } else {
      const params = {
        credentials: {password: '', username: ''},
        credentials_name: carrier,
      }
      await backendApis.createSecret(params);
    }
    toast.success('Connection removed successfully');
    setConnectionStatus((prev: any) => ({...prev, [carrier]: {connected: false, loading: false}}));
  };

  if (!organization) {
    return <Loading/>;
  }

  const RemoveCarrierDialog = ({carrier}: {carrier: CarrierAccess}) => {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size='xs'
            variant="destructive"
          >
            Remove
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRemove(carrier.carrier_name)}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  if (createConnectionCarrier) {
    return (
      <div className="container mx-auto">
        {/*<ConnectionCreation*/}
        {/*  cancel={() => setCreateConnectionCarrier(null)}*/}
        {/*  carrier={createConnectionCarrier}*/}
        {/*  key={createConnectionCarrier.carrier_name}*/}
        {/*  updateCarrierStatus={updateCarrierStatus}*/}
        {/*/>*/}
        <SimpleConnectionCreation
          cancel={() => setCreateConnectionCarrier(null)}
          carrier={createConnectionCarrier}
          key={createConnectionCarrier.carrier_name}
          updateCarrierStatus={updateCarrierStatus}
        />
      </div>
    )
  }

  const tableHeadClassname = 'font-normal text-sm border-b text-left';

  function connectionStatusText(status: ConnectionStatus) {
    switch (status) {
      case ConnectionStatus.CHECKING:
        return 'Checking..';
      case ConnectionStatus.CONNECTED:
        return 'Connected';
      case ConnectionStatus.LOGIN_ERROR:
        return 'Login Error';
      case ConnectionStatus.NOT_CONNECTED:
        return 'Not Connected';
      default:
        return '';
    }
  }

  return (
    <Card className='bg-white'>
      <CardHeader>
        Connections
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {
                ['Platform', 'Status', 'Action'].map((title) => (
                  <TableHead
                    className={tableHeadClassname}
                    key={title}
                  >
                    {title}
                  </TableHead>
                ))
              }
            </TableRow>
          </TableHeader>
          <TableBody>
            {
              allCarrierAccess.map((carrier) => {
                const carrierStatus = connectionStatus[carrier.carrier_name] || {
                  browserStateExists: false,
                  connected: false,
                  credentialsExists: false,
                  loading: true
                };
                const status = carrierStatus.loading ? ConnectionStatus.CHECKING : (
                  carrierStatus.connected ? ConnectionStatus.CONNECTED : (
                    carrierStatus.credentialsExists ? ConnectionStatus.LOGIN_ERROR : ConnectionStatus.NOT_CONNECTED
                  )
                );

                return (
                  <TableRow key={carrier.carrier_name}>
                    <TableCell>
                      {formatTitle(carrier.carrier_name)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={connectedStatusColors(status)}
                      >
                        {connectionStatusText(status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {carrierStatus.loading ? (
                        <Skeleton className='w-12 md:w-24 h-6 bg-gray-500'/>
                      ) : carrierStatus.connected ? (
                        <RemoveCarrierDialog carrier={carrier}/>
                      ) : (
                        <div className='space-x-3'>
                          <Button
                            onClick={() => setCreateConnectionCarrier(carrier)}
                            size='xs'
                          >
                            Connect
                          </Button>
                          {
                            carrierStatus.credentialsExists && (
                              <RemoveCarrierDialog carrier={carrier}/>
                            )
                          }
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            }
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
};

export default Connections;
